import { InstagramHandler } from "./InstagramHandler";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';

export interface FooterProps {
  footer_bg: string;
  text_color?: string; // Add optional text color prop
  currentService?: 'fabric' | 'cardetailing' | 'aircond' | 'sanitise' | 'pestcontrol';
}

const Footer = ({ footer_bg, text_color = 'text-white', currentService }: FooterProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const isFabricPage = currentPath.includes('/services/fabric');

  // Use this condition for text coloring
  const textColorClass = isFabricPage ? 'text-black' : text_color;

  const handleScrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const socialHandles = [
    { handle: '@trhine_cardetailing', service: 'cardetailing' },
    { handle: '@trhine_aircondservices', service: 'aircond' },
    { handle: '@trhine_fabriccleaning', service: 'fabric' },
    { handle: '@trhine_disinfection', service: 'sanitise' },
    { handle: '@trhinepestcontrol', service: 'pestcontrol' },
  ];

  // Filter social handles based on current service
  const displayedHandles = currentService 
    ? socialHandles.filter(handle => handle.service === currentService)
    : socialHandles;

  return (
    <div className={`${footer_bg} ${textColorClass} flex flex-col`}>
      <div className="flex flex-col p-6 md:flex-row md:px-12 md:py-8 lg:px-16 lg:py-10">
        <div className="flex-1 flex flex-col">
          <div>
            <h1 className={`text-xl md:text-2xl font-bold ${textColorClass}`}>TRhine Services</h1>
            <p className={`text-sm md:text-base mt-1 ${textColorClass}`}>{t('footer.tagline')}</p>
          </div>
          <div className="mt-4 md:mt-6 flex flex-col gap-1.5">
            {displayedHandles.map((social, index) => (
              <InstagramHandler key={index} handle={social.handle} url={`https://www.instagram.com/${social.handle.replace('@', '')}/`} textColor={textColorClass} />
            ))}
          </div>
        </div>
        <div className="basis-1/4 mt-6 md:mt-0">
          <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4">{t('footer.navigation')}</h2>
          <ul className="flex flex-col gap-2">
              <li>
                <Link to="/" className={`hover:underline cursor-pointer text-sm md:text-base transition-all ${textColorClass}`}>{t('footer.home')}</Link>
              </li>
              <li>
                <button
                  onClick={() => handleScrollToSection('services')}
                  className={`hover:underline cursor-pointer text-left text-sm md:text-base bg-transparent border-none transition-all ${textColorClass}`}
                >
                  {t('footer.services')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleScrollToSection('contact')}
                  className={`hover:underline cursor-pointer text-left text-sm md:text-base bg-transparent border-none transition-all ${textColorClass}`}
                >
                  {t('footer.contact')}
                </button>
              </li>
          </ul>
        </div>
        <div className="basis-1/4 mt-6 md:mt-0">
          <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4">{t('footer.servicesSection')}</h2>
          <ul className={`footer-services-list ${currentService === 'fabric' ? 'text-black' : 'text-white'}`}>
              <li>
                <Link to="/services/car-polish" className={`hover:underline cursor-pointer text-sm md:text-base transition-all ${textColorClass}`}>{t('services.carPolish')}</Link>
              </li>
              <li className="mt-2">
                <Link to="/services/fabric" className={`hover:underline cursor-pointer text-sm md:text-base transition-all ${textColorClass}`}>{t('services.fabricCleaning')}</Link>
              </li>
              <li className="mt-2">
                <Link to="/services/aircond" className={`hover:underline cursor-pointer text-sm md:text-base transition-all ${textColorClass}`}>{t('services.aircond')}</Link>
              </li>
              <li className="mt-2">
                <Link to="/services/sanitise" className={`hover:underline cursor-pointer text-sm md:text-base transition-all ${textColorClass}`}>{t('services.sanitise')}</Link>
              </li>
              <li className="mt-2">
                <Link to="/services/pest-control" className={`hover:underline cursor-pointer text-sm md:text-base transition-all ${textColorClass}`}>{t('services.pestControl')}</Link>
              </li>
          </ul>
        </div>
      </div>
      <div className={`border-t ${isFabricPage ? 'border-gray-300' : 'border-white/20'} mx-6 md:mx-12 lg:mx-16 py-4 text-center`}>
        <p className={`text-xs md:text-sm ${textColorClass} opacity-70`}>&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
      </div>
    </div>
  )
}

export default Footer